<script>
  import Template from "$app/AppTemplate.svelte";
  import { onMount, onDestroy } from "svelte";
  import { Button } from "$lib/components/ui/button";
  import ProfitChart from "$app/ProfitChart.svelte";
  import DecisionButton from "$app/DecisionButton.svelte";
  import PlayerAction from "$app/PlayerAction.svelte";

  import CardSVG from "$app/CardSVG.svelte";
  import ChipVisual from "$app/ChipVisual.svelte";
  import PokerTable from "$app/PokerTable.svelte";
  import ResponsiveDialog from "$app/ResponsiveDialog.svelte";
  import HandReview from "$app/HandReview.svelte";
  import { callFunction } from "$lib/api";
  import { loadConfig } from "$lib/utils/helpers.js";
  import { account, getAccount } from "$lib/stores/state.svelte.js";
  import { evaluateCards } from "phe";
  import { Hand } from "pokersolver";
  import {
    parseAction,
    colorForAction,
    cardsToHandStr,
    cardToString,
    shuffle,
    chooseIndexWeighted,
    applySuitShift,
    applyFlopSwap,
    rowColToHandIndex,
    rowColToHandStr,
    handStrToHands,
    handStrWeightMultiplier,
    lastAction,
  } from "$app/poker-helpers.js";
  import { loadAllResults, addResult, deleteAllResults } from "$lib/db";
  import { trackEvent } from "$lib/analytics";
  import Trash2Icon from "@lucide/svelte/icons/trash-2";
  import Loader2 from "@lucide/svelte/icons/loader-2";
  import DownloadIcon from "@lucide/svelte/icons/download";
  import UploadIcon from "@lucide/svelte/icons/upload";

  const preflopPrefix = `preflop_2_200`;
  const playerName = "player";
  const opponentName = "opponent";
  let state = $state("preflop");
  let preflopData = $state(null);
  let playerIndex = $state(null); // which player index is the human player (the user)
  let opponentIndex = $state(null); // which player index is the opponent (the bot)
  let hands = $state(null);
  let board = $state(null);
  let sequence = $state(null);
  const PAGE_SIZE = 25;
  let results = $state([]);
  let result = $state(null);
  let resultsPage = $state(0);
  let playerPositions = $state([]);
  let playerNames = $state([]);
  let actionIndices = $state([]);
  let solutionFile = $state(null);
  let gameGraph = $state(null);
  let suitShift = $state(null);
  let flopSwap = $state(null);
  let decisionPlayer = $state(null);
  let privateIndices = $state(null);
  let node = $state(null);
  let choices = $state(null);
  let choiceClicked = $state(null);
  let outcomes = $state(null);
  let strategy = $state(null);
  let strategyHand = $state(null);
  let range = $state(null);
  let pot = $state(null);
  let chips = $state(null);
  let preflopCommitted = $state(null);
  let solution = $state(null);
  let actionValues = $state(null); // kept for interface compat, always null now
  let childValues = $state(null); // per-action EVs for the current hand (postflop only)
  let mistakeTracked = $state(false);
  let consecutiveOpponentActions = $state(0);
  const MAX_CONSECUTIVE_OPPONENT_ACTIONS = 50;
  let handTables = $state(null);
  let categoryTables = $state(null);
  let overalls = $state(null);
  let maxTableWeights = $state(null);
  let maxCategoryWeights = $state(null);
  let maxWeights = $state(null);
  let selectedHands = $state(null);
  let activeTab = $state("actions");
  let reviewResult = $state(null);
  let isPortrait = $state(false);
  let portraitMQL = null;
  let portraitHandler = null;
  let showClearDialog = $state(false);
  let clearing = $state(false);
  let showCreditsExhaustedDialog = $state(false);
  let showLoginDialog = $state(false);
  let fileInput = $state(null);

  const config = loadConfig();

  const HAND_SAVE_KEY = "actionflop_in_progress_hand";

  /**
   * Save the current in-progress hand state to localStorage.
   * Only saves if the hand is actively in progress (not complete, not null).
   */
  function saveInProgressHand() {
    // Never save guest demo state — logged-in users only
    if (state === "complete" || !hands || !preflopData || !account.id) return;
    try {
      const saveData = {
        state,
        hands,
        board,
        sequence,
        playerIndex,
        opponentIndex,
        playerNames,
        actionIndices,
        solutionFile,
        gameGraph,
        suitShift,
        flopSwap,
        decisionPlayer,
        privateIndices,
        node,
        choices,
        outcomes,
        strategy,
        range,
        pot,
        chips,
        preflopCommitted,
        handTables,
        categoryTables,
        overalls,
        solution,
        maxTableWeights,
        maxCategoryWeights,
        maxWeights,
        timestamp: Date.now(),
      };
      localStorage.setItem(HAND_SAVE_KEY, JSON.stringify(saveData));
    } catch (e) {
      console.error("Failed to save hand to localStorage:", e);
    }
  }

  /**
   * Load a saved in-progress hand from localStorage.
   * Returns null if no saved hand exists or if it's too old (>24 hours).
   */
  function loadInProgressHand() {
    try {
      const raw = localStorage.getItem(HAND_SAVE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      // Expire saves older than 24 hours
      if (Date.now() - data.timestamp > 24 * 60 * 60 * 1000) {
        localStorage.removeItem(HAND_SAVE_KEY);
        return null;
      }
      return data;
    } catch (e) {
      console.error("Failed to load hand from localStorage:", e);
      return null;
    }
  }

  /**
   * Clear the saved in-progress hand from localStorage.
   */
  function clearSavedHand() {
    try {
      localStorage.removeItem(HAND_SAVE_KEY);
    } catch (e) {
      console.error("Failed to clear saved hand:", e);
    }
  }

  /**
   * Restore game state from a saved hand object.
   */
  async function restoreHand(data) {
    state = data.state;
    hands = data.hands;
    board = data.board;
    sequence = data.sequence;
    playerIndex = data.playerIndex;
    opponentIndex = data.opponentIndex;
    playerNames = data.playerNames;
    actionIndices = data.actionIndices;
    solutionFile = data.solutionFile;
    gameGraph = data.gameGraph;
    suitShift = data.suitShift;
    flopSwap = data.flopSwap || [0, 1, 2];
    decisionPlayer = data.decisionPlayer;
    privateIndices = data.privateIndices;
    node = data.node;
    choices = data.choices;
    outcomes = data.outcomes;
    strategy = data.strategy;
    range = data.range;
    pot = data.pot;
    chips = data.chips;
    preflopCommitted = data.preflopCommitted;
    handTables = data.handTables;
    categoryTables = data.categoryTables;
    overalls = data.overalls;
    solution = data.solution;
    maxTableWeights = data.maxTableWeights;
    maxCategoryWeights = data.maxCategoryWeights;
    maxWeights = data.maxWeights;
    choiceClicked = false;
    result = null;
    selectedHands = null;
    // Reset mistake tracking — will be re-evaluated from the restored sequence
    mistakeTracked = false;
    actionValues = null;

    // Recompute strategyHand from strategy + current hand
    if (strategy && decisionPlayer !== null && hands) {
      const handStr = cardsToHandStr(hands[decisionPlayer]);
      strategyHand = strategy[handStr];
    }

    // Continue processing from the restored state (handles chance nodes,
    // opponent decisions, etc. to reach a stable player-decision point)
    await iterateDecisions();
  }

  /**
   * Build a minimal preflop data structure for guest demo mode.
   * Values match the real preflop_2_200.json data file.
   * No API calls needed — everything is hardcoded.
   */
  function buildGuestPreflopData() {
    // Match preflop_2_200.json: stacks=200, bb=2, positions=[bn, bb]
    const stacks = 200;
    const bb = 2;
    const sb = 1;
    const positions = ["bn", "bb"];

    // Actions match the real solver output: fold, call, raise to 4x BB
    const choices = ["fold", "call", { raise: bb * 2 }];

    // Demo hand for the player (BN): AA (pocket aces)
    const demoHand = "AA";
    // Strategy as [fold%, call%, raise%] — matches solver format
    const demoStrategy = [0.0, 0.15, 0.85];

    // Build a uniform range (all 169 hand combos, weight 1.0)
    const range = {};
    for (let i = 0; i < 13; i++) {
      for (let j = i; j < 13; j++) {
        const h = rowColToHandStr(i, j);
        range[h] = 1.0;
      }
    }

    // Build strategy map — demo hand gets aggressive strategy, rest fold
    const strategy = {};
    for (const h of Object.keys(range)) {
      strategy[h] = h === demoHand ? demoStrategy : [1.0, 0.0, 0.0];
    }

    return {
      stacks,
      bb,
      positions,
      decisionPoints: [
        {
          actionSequence: [
            { player: 0, action: { blind: sb } },
            { player: 1, action: { blind: bb } },
          ],
          pot: bb + sb,
          chips: [stacks - sb, stacks - bb],
          player: 0,
          actions: choices,
          strategy,
          range,
        },
      ],
      lines: [],
    };
  }

  /**
   * Set up a dummy hand for guests who are not logged in.
   * Fully self-contained — no API calls, no preflop_ranges dependency.
   */
  function setupGuestDemo() {
    preflopData = buildGuestPreflopData();
    playerPositions = preflopData.positions;

    playerIndex = 0; // BN — acts first preflop
    opponentIndex = 1; // BB

    dealHoleCards(playerPositions.length);
    // Always give the player pocket aces (cards 48=A♠, 49=A♥)
    // Encoding: card = rank * 4 + suit, rank 12 = Ace
    hands[playerIndex] = [48, 49];
    // Clear any saved hand state since guest demo should never persist
    clearSavedHand();

    board = [];
    selectedHands = null;
    solutionFile = null;
    suitShift = {};
    let suits = [0, 1, 2, 3];
    shuffle(suits);
    for (let i = 0; i < 4; i += 1) {
      suitShift[i] = suits[i];
    }
    flopSwap = [0, 1, 2];
    shuffle(flopSwap);
    sequence = [
      { action: { blind: preflopData.bb / 2 }, player: 0 },
      { action: { blind: preflopData.bb }, player: 1 },
    ];
    playerNames = playerPositions.map((x, i) => {
      if (i === playerIndex) return playerName;
      else return opponentName;
    });
    state = "preflop";
    result = null;
    // Guest demo — show fold/call/raise; clicking any shows login dialog
    pot = preflopData.bb + preflopData.bb / 2;
    chips = [200 - preflopData.bb / 2, 200 - preflopData.bb];
    decisionPlayer = 0; // BN decides first, matching the real solver
    choices = ["fold", "call", { raise: 4 }];
    const dp = preflopData.decisionPoints[0];
    strategy = dp.strategy;
    range = dp.range;
    const handStr = cardsToHandStr(hands[playerIndex]);
    strategyHand = strategy[handStr];
    handTables = null;
    categoryTables = null;
    overalls = null;
  }

  /**
   * Handle a callFunction result. If it's an insufficient_credits error:
   * - "no_subscription" → redirect to /thanks-for-playing
   * - "credits_exhausted" → show the credits exhausted dialog
   * Returns the result if successful, otherwise handles the error and returns null.
   */
  async function handleCallFunction(name, body) {
    const result = await callFunction(name, body);
    if (result.Error) {
      // Try to parse the error as JSON (structured error from backend)
      try {
        const parsed = JSON.parse(result.Error.message);
        if (parsed.error === "insufficient_credits") {
          if (parsed.reason === "no_subscription") {
            trackEvent("thanks-for-playing");
            window.location.href = "/thanks-for-playing";
          } else if (parsed.reason === "credits_exhausted") {
            showCreditsExhaustedDialog = true;
          }
          return null;
        }
      } catch {
        // Not a structured JSON error — fall through
      }
      // Unknown error — just return null
      return null;
    }
    return result;
  }
  const tabItems = [
    { value: "actions", label: "Actions" },
    { value: "results", label: "Results" },
  ];

  onMount(async () => {
    try {
      const savedResults = await loadAllResults();
      if (savedResults && savedResults.length > 0) {
        results = savedResults;
      }
    } catch (e) {
      console.error("Failed to load results from IndexedDB:", e);
    }

    // Ensure account state is resolved before deciding guest vs authenticated
    await getAccount();

    // Guests get a fully self-contained demo with no API calls
    if (!account.id) {
      setupGuestDemo();
    } else {
      // Authenticated user: load preflop data, restore saved hand if any
      const savedHand = loadInProgressHand();
      const result = await handleCallFunction("preflop_ranges", {
        filename: preflopPrefix,
      });
      if (result && result.Success) {
        preflopData = result.Success.preflopLines;
        playerPositions = preflopData.positions;

        if (savedHand) {
          await restoreHand(savedHand);
        } else {
          await newHand();
        }
      }
    }

    // Save in-progress hand on page close/refresh
    window.addEventListener("beforeunload", saveInProgressHand);

    // Detect portrait/landscape orientation
    const mql = window.matchMedia("(orientation: portrait)");
    portraitMQL = mql;
    isPortrait = mql.matches;
    portraitHandler = (e) => {
      isPortrait = e.matches;
    };
    mql.addEventListener("change", portraitHandler);
  });

  onDestroy(() => {
    if (portraitMQL && portraitHandler) {
      portraitMQL.removeEventListener("change", portraitHandler);
    }
    window.removeEventListener("beforeunload", saveInProgressHand);
    // Save in-progress hand when leaving the page
    saveInProgressHand();
  });

  const totalProfit = $derived.by(() =>
    results.map((x) => x.profits["player"]).reduce((a, b) => a + b, 0),
  );

  const totalMistakes = $derived.by(
    () => results.filter((x) => x.mistakeLoss != null).length,
  );

  const cumulativeProfitData = $derived.by(() => {
    if (results.length === 0) return [[], []];

    const hands = [];
    const profits = [];
    let runningTotal = 0;
    for (let i = 0; i < results.length; i++) {
      runningTotal += results[i].profits["player"];
      hands.push(i + 1);
      profits.push(runningTotal);
    }

    if (hands.length > 200) {
      // Sample down to ~100 points, always including first and last
      const targetPoints = 100;
      const sampled = [0];
      const step = (hands.length - 1) / (targetPoints - 1);
      for (let i = 1; i < targetPoints - 1; i++) {
        sampled.push(Math.floor(i * step));
      }
      // Ensure last point is included (avoid duplicates from floating point)
      const last = sampled[sampled.length - 1];
      if (last !== hands.length - 1) {
        sampled.push(hands.length - 1);
      }

      return [
        sampled.map((idx) => hands[idx]),
        sampled.map((idx) => profits[idx]),
      ];
    }

    return [hands, profits];
  });

  const totalResultsPages = $derived(
    Math.max(1, Math.ceil(results.length / PAGE_SIZE)),
  );
  const paginatedResults = $derived(
    results
      .toReversed()
      .slice(resultsPage * PAGE_SIZE, (resultsPage + 1) * PAGE_SIZE),
  );

  function dealHoleCards(nPlayers) {
    const deck = Array.from({ length: 52 }, (_, i) => i);
    shuffle(deck);
    const newHands = [];
    for (let i = 0; i < nPlayers; i++) {
      newHands.push([deck[i * 2], deck[i * 2 + 1]]);
    }
    hands = [...newHands];
  }

  function dealBoardCards(n) {
    const existingCards = Object.fromEntries(
      board.concat(hands.flat()).map((x) => [x, true]),
    );
    const deck = Array.from({ length: 52 }, (_, i) => i).filter(
      (x) => !existingCards[String(x)],
    );
    shuffle(deck);
    const newBoard = [...board];
    for (let i = 0; i < n; i += 1) {
      newBoard.push(deck[i]);
    }
    board = [...newBoard];
    if (newBoard.length >= 3) {
      sequence.push({
        chance: newBoard.slice(0, 3),
      });
      for (const card of newBoard.slice(3)) {
        sequence.push({
          chance: [card],
        });
      }
    } else {
      for (const card of newBoard) {
        sequence.push({
          chance: [card],
        });
      }
    }
  }

  async function newHand() {
    clearSavedHand();
    mistakeTracked = false;
    dealHoleCards(playerPositions.length);
    selectedHands = null;
    solutionFile = null;
    suitShift = {};
    let suits = [0, 1, 2, 3];
    shuffle(suits);
    for (let i = 0; i < 4; i += 1) {
      suitShift[i] = suits[i];
    }
    board = [];
    flopSwap = [0, 1, 2];
    shuffle(flopSwap);
    sequence = [
      { action: { blind: preflopData.bb / 2 }, player: 0 },
      { action: { blind: preflopData.bb }, player: 1 },
    ];
    playerIndex =
      playerIndex != null ? (playerIndex + 1) % playerPositions.length : 0;
    opponentIndex = (playerIndex + 1) % playerPositions.length;
    playerNames = playerPositions.map((x, i) => {
      if (i === playerIndex) {
        return playerName;
      } else {
        return opponentName;
      }
    });
    state = "preflop";
    result = null;
    choices = null;
    const handStr = cardsToHandStr(hands[playerIndex]);
    await iterateDecisions();
  }

  async function clearAllResults() {
    clearing = true;
    try {
      await deleteAllResults();
      results = [];
      resultsPage = 0;
    } catch (e) {
      console.error("Failed to clear results from IndexedDB:", e);
    } finally {
      clearing = false;
      showClearDialog = false;
    }
  }

  function downloadResults() {
    if (results.length === 0) return;
    const json = JSON.stringify(results, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `poker-hands-${new Date().toISOString().split("T")[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function handleFileUpload(event) {
    const file = event.target.files[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const resultsArray = Array.isArray(data) ? data : [data];
      for (const result of resultsArray) {
        await addResult(result);
      }
      const savedResults = await loadAllResults();
      if (savedResults && savedResults.length > 0) {
        results = savedResults;
      }
      resultsPage = 0;
      trackEvent("results_uploaded", { count: resultsArray.length });
    } catch (e) {
      console.error("Failed to upload results:", e);
    }
    event.target.value = "";
  }

  function determineWinners() {
    const boardPlusHands = hands.map((x) =>
      board
        .concat(x)
        .map((x) => cardToString(applySuitShift(suitShift, x), true)),
    );
    const handDescriptions = boardPlusHands.map((x) => Hand.solve(x).descr);
    // console.log(boardPlusHands, handDescriptions);
    const handRanks = boardPlusHands.map((x) => evaluateCards(x)); // lower is better
    const lowestRank = Math.min(...handRanks);
    const winners = {};
    for (let i = 0; i < playerPositions.length; i += 1) {
      if (handRanks[i] <= lowestRank) {
        winners[i] = true;
      }
    }
    return winners;
  }

  function recordResult(winners, pot, committed) {
    const numWinners = Object.values(winners).filter((x) => x).length;
    const profits = {};
    for (let i = 0; i < playerPositions.length; i += 1) {
      if (winners[i]) {
        profits[playerNames[i]] = pot / numWinners - committed[i];
      } else {
        profits[playerNames[i]] = 0 - committed[i];
      }
    }
    result = {
      sequence,
      profits,
      mistakeLoss:
        sequence?.find((e) => e.mistake && e.player === playerIndex)?.mistakeLoss ?? null,
      playerCards: [...hands[playerIndex]],
      opponentCards: [...hands[opponentIndex]],
      boardCards: [...board],
      suitShift: { ...suitShift },
      flopSwap: flopSwap ? [...flopSwap] : null,
      solutionFile: solutionFile || null,
      playerIndex,
      opponentIndex,
    };
    results.push(result);
    addResult($state.snapshot(result)).catch((e) =>
      console.error("Failed to persist result to IndexedDB:", e),
    );
    // console.log($state.snapshot(result));
  }

  async function getSolution(filename, newActionIndices) {
    // console.log(hands.map((x) => x.map((c) => cardToString(c))));
    const functionResult = await handleCallFunction("play_explore", {
      actionIndices: newActionIndices,
      filename,
      hands,
    });
    if (functionResult && functionResult.Success) {
      const exploreResult = functionResult.Success;
      // Only update state on success so that a failed API call doesn't leave
      // us with a stale node paired with advanced actionIndices (which could
      // cause the opponent-auto-play loop to re-request with ever-growing
      // indices and never make progress).
      actionIndices = [...newActionIndices];
      gameGraph = exploreResult.gameGraph;
      node = exploreResult.currentNode;
      strategy = exploreResult.strategy ?? null;
      actionValues = null;
      childValues = exploreResult.childActionValues ?? null;
      // Use backend's pre-computed range tables
      if (exploreResult.rangeTables) {
        handTables = exploreResult.rangeTables.handTables;
        categoryTables = exploreResult.rangeTables.categoryTables;
        maxTableWeights = exploreResult.rangeTables.maxTableWeights;
        maxCategoryWeights = exploreResult.rangeTables.maxCategoryWeights;
      } else {
        handTables = null;
        categoryTables = null;
        maxTableWeights = null;
        maxCategoryWeights = null;
      }
      // console.log($state.snapshot(strategy));
    } else {
      console.error(
        "play_explore failed, starting a new hand:",
        functionResult?.Error?.message ?? "unknown error",
      );
      await newHand();
    }
  }

  async function iterateDecisions() {
    if (state === "preflop") {
      const decisionPoints = preflopData.decisionPoints.filter(
        (x) =>
          sequence.length === x.actionSequence.length &&
          sequence.every((s, i) => {
            const a = x.actionSequence[i];
            return (
              s.player === a.player &&
              JSON.stringify(s.action) === JSON.stringify(a.action)
            );
          }),
      );
      maxTableWeights = null;
      maxCategoryWeights = null;
      maxWeights = null;
      handTables = null;
      categoryTables = null;
      overalls = null;
      if (decisionPoints.length === 1) {
        const decisionPoint = decisionPoints[0];
        pot = decisionPoint.pot;
        chips = decisionPoint.chips;
        decisionPlayer = decisionPoint.player;
        choices = decisionPoint.actions;
        choiceClicked = false;
        strategy = decisionPoint.strategy;
        const handStr = cardsToHandStr(hands[decisionPlayer]);
        strategyHand = strategy[handStr];
        range = decisionPoint.range;
        maxTableWeights = playerPositions.map((_) => 0);
        handTables = playerPositions.map((_) => null);
        for (let player = 0; player < playerPositions.length; player += 1) {
          let table = new Array(169).fill(null).map((_) => {
            return {
              weight: 0,
              hands: [],
              strategy: null,
            };
          });
          let maxWeight = 0;
          for (let i = 0; i < 13; i += 1) {
            for (let j = 0; j < 13; j += 1) {
              const handStr = rowColToHandStr(i, j);
              const index = rowColToHandIndex(i, j);
              const weight = range[handStr] * handStrWeightMultiplier(handStr);
              const s = player === decisionPlayer ? strategy[handStr] : null;
              if (weight > maxWeight) {
                maxWeight = weight;
              }
              const handStrHands = handStrToHands(handStr);
              const hands = handStrHands.map((hand) => {
                return {
                  hand,
                  strategy: s,
                  weight: weight / handStrHands.length,
                };
              });
              table[index] = {
                hands,
                weight,
                strategy: s,
              };
            }
          }
          handTables[player] = table;
          maxTableWeights[player] = maxWeight;
        }
        if (decisionPlayer === playerIndex) {
          // player decision — reset the consecutive opponent counter
          consecutiveOpponentActions = 0;
        } else {
          // opponent decision — detect infinite loops
          consecutiveOpponentActions++;
          if (consecutiveOpponentActions > MAX_CONSECUTIVE_OPPONENT_ACTIONS) {
            console.error(
              `Infinite loop detected (preflop): ${consecutiveOpponentActions} consecutive opponent actions. Force-terminating hand.`,
            );
            sequence.push({ terminal: true });
            state = "complete";
            clearSavedHand();
            choices = null;
            strategy = null;
            range = null;
            decisionPlayer = null;
            return;
          }
          const choiceIndex = chooseIndexWeighted(strategy[handStr]);
          const action = choices[choiceIndex];
          const mistake = strategyHand[choiceIndex] == 0;
          sequence.push(
            $state.snapshot({
              player: decisionPlayer,
              action,
              choices,
              // handTables,
              // maxTableWeights,
              // strategy,
              // range,
              // strategyHand,
              mistake,
            }),
          );
          await iterateDecisions();
        }
      } else {
        const lines = preflopData.lines.filter(
          (x) =>
            sequence.length === x.actionSequence.length &&
            sequence.every((s, i) => {
              const a = x.actionSequence[i];
              return (
                s.player === a.player &&
                JSON.stringify(s.action) === JSON.stringify(a.action)
              );
            }),
        );
        if (lines.length === 1) {
          const line = lines[0];
          pot = line.pot;
          chips = line.chips;
          if (line.kind === "terminal") {
            // terminal because someone folded
            consecutiveOpponentActions = 0;
            state = "complete";
            clearSavedHand();
            choices = null;
            strategy = null;
            range = null;
            decisionPlayer = null;
            const winners = new Array(playerPositions.length).fill(true);
            for (const { action, player } of line.actionSequence) {
              if (action === "fold") {
                winners[player] = false;
              }
            }
            recordResult(winners, line.pot, line.committed);
          } else if (line.kind === "chance") {
            if (line.chips.reduce((a, b) => a + b, 0) === 0) {
              // the players are all in
              consecutiveOpponentActions = 0;
              state = "complete";
              clearSavedHand();
              choices = null;
              strategy = null;
              range = null;
              decisionPlayer = null;
              dealBoardCards(5);
              const winners = determineWinners();
              recordResult(winners, line.pot, line.committed);
            } else {
              // the players still have chips, proceed to flop
              consecutiveOpponentActions = 0;
              choices = null;
              strategy = null;
              range = null;
              decisionPlayer = null;
              preflopCommitted = [...line.committed];
              const actionSequenceString = line.actionSequence
                .map(({ action }) => {
                  const { kind, value } = parseAction(action);
                  return kind.replaceAll(" ", "").toLowerCase();
                })
                .join("-");
              const solutionPrefix = `${preflopPrefix}-${playerPositions.map((x, i) => String(i)).join("-")}-${actionSequenceString}`;
              const result = await handleCallFunction("select_solution", {
                prefix: solutionPrefix,
                cards: hands.flat(),
              });
              if (result && result.Success) {
                solutionFile = result.Success.filename;
                state = "postflop";
                await getSolution(solutionFile, []);
                await iterateDecisions();
              }
            }
          }
        } else {
          throw new Error("Failed to find matching preflop line");
        }
      }
    } else if (state === "postflop") {
      if (gameGraph && node) {
        if (actionIndices.length === 0) {
          // `play_explore` may omit `privates` from the response when hands
          // are provided (hand-specific strategy is returned directly).
          // Only compute privateIndices when privates are available.
          if (gameGraph.players[0]?.privates) {
            const newPrivateIndices = new Array(hands.length).fill(-1);
            for (let i = 0; i < hands.length; i += 1) {
              const hand = hands[i];
              for (
                let j = 0;
                j < gameGraph.players[i].privates.length;
                j += 1
              ) {
                const privateHand = gameGraph.players[i].privates[j];
                if (
                  privateHand.toSorted().toString() ===
                  hand.toSorted().toString()
                ) {
                  newPrivateIndices[i] = j;
                }
              }
            }
            privateIndices = newPrivateIndices;
          }
          if (node && node.node && node.node.decision) {
            const data = node.node.decision.data;
            const flop = data.board;
            sequence.push({
              chance: flop,
            });
          }
        }
        maxTableWeights = null;
        maxCategoryWeights = null;
        maxWeights = null;
        handTables = null;
        categoryTables = null;
        overalls = null;
        if (node?.node?.decision) {
          const decision = node?.node?.decision;
          decisionPlayer = decision.player;
          choices = decision.choices;
          choiceClicked = false;
        } else {
          decisionPlayer = null;
          choices = null;
        }
        // Range tables are now provided by the backend via play_explore.rangeTables
        if (node.node && node.node.decision) {
          // decision node
          const decision = node.node.decision;
          const data = node.node.decision.data;
          pot = data.pot;
          chips = data.chips;
          // console.log($state.snapshot(gameGraph), $state.snapshot(node));
          board = data.board;
          // `strategy` is already set by `getSolution` (hand-specific from
          // `play_explore`) or null at non-decision nodes.
          if (strategy) {
            strategyHand = strategy;
          } else {
            // Fallback when no hand-specific strategy is available:
            // prefer the most conservative action (fold > check > others).
            // If no fold/check exists, default to the first choice (index 0).
            const fallback = decision.choices.map((x) =>
              x === "fold" || x === "check" ? 1 : 0,
            );
            const sum = fallback.reduce((a, b) => a + b, 0);
            strategyHand = sum > 0 ? fallback : null;
          }
          if (playerIndex == decisionPlayer) {
            // player decision — reset the consecutive opponent counter
            consecutiveOpponentActions = 0;
          } else {
            // opponent decision — detect infinite loops
            consecutiveOpponentActions++;
            if (consecutiveOpponentActions > MAX_CONSECUTIVE_OPPONENT_ACTIONS) {
              console.error(
                `Infinite loop detected: ${consecutiveOpponentActions} consecutive opponent actions. Force-terminating hand.`,
              );
              sequence.push({ terminal: true });
              state = "complete";
              clearSavedHand();
              choices = null;
              strategy = null;
              range = null;
              decisionPlayer = null;
              return;
            }
            const choiceIndex = strategyHand
              ? chooseIndexWeighted(strategyHand)
              : 0;
            const action = choices[choiceIndex];
            const mistake = strategyHand
              ? strategyHand[choiceIndex] == 0
              : false;
            sequence.push(
              $state.snapshot({
                player: decisionPlayer,
                action,
                choices,
                // handTables,
                // maxTableWeights,
                // categoryTables,
                // maxCategoryWeights,
                // strategyHand,
                mistake,
              }),
            );
            await getSolution(solutionFile, actionIndices.concat(choiceIndex));
            await iterateDecisions();
          }
        } else if (node && node.node && node.node.chance) {
          // chance node — reset the consecutive opponent counter because this
          // represents progress in the game tree
          consecutiveOpponentActions = 0;
          const data = node.node.chance.data;
          pot = data.pot;
          chips = data.chips;
          board = data.board;
          outcomes = [];
          const deadCards = Object.fromEntries(
            hands
              .flat()
              .concat(board)
              .map((x) => [x, true]),
          );
          // console.log(
          //   hands
          //     .flat()
          //     .concat(board)
          //     .map((x) => [x, true]),
          // );
          // console.log($state.snapshot(hands));
          // console.log(new array(52).fill(0).map((_, i) => cardtostring(i)));
          // console.log(deadcards);
          for (
            let i = 0;
            i < node.node.chance.isomorphismCards.length;
            i += 1
          ) {
            const isocard = node.node.chance.isomorphismCards[i];
            const index = node.node.chance.isomorphismRefs[i];
            const card = node.node.chance.outcomes[index];
            if (!deadCards[card]) {
              outcomes.push([card, index]);
            }
          }
          for (let i = 0; i < node.node.chance.outcomes.length; i += 1) {
            const card = node.node.chance.outcomes[i];
            const index = i;
            if (!deadCards[card]) {
              outcomes.push([card, index]);
            }
          }
          const r = Math.floor(Math.random() * outcomes.length);
          const [card, index] = outcomes[r];
          sequence.push({
            chance: [card],
            index,
          });
          await getSolution(solutionFile, actionIndices.concat(index));
          await iterateDecisions();
        } else if (node && node.node && node.node.terminal) {
          // terminal node
          consecutiveOpponentActions = 0;
          const data = node.node.terminal.data;
          pot = data.pot;
          chips = data.chips;
          sequence.push({
            terminal: true,
            // handTables,
            // maxTableWeights,
            // categoryTables,
            // maxCategoryWeights,
          });
          const committed = preflopCommitted.map(
            (x, i) => x + data.committed[i],
          );
          board = data.board;
          state = "complete";
          clearSavedHand();
          choices = null;
          strategy = null;
          range = null;
          decisionPlayer = null;
          if (data.folded.some((x) => x)) {
            // terminal because someone folded
            const winners = new Array(playerPositions.length).fill(true);
            for (const { action, player } of sequence) {
              if (action === "fold") {
                winners[player] = false;
              }
            }
            recordResult(winners, data.pot, committed);
          } else {
            // terminal due to showdown
            if (data.board.length < 5) {
              // deal remaining board cards
              dealBoardCards(5 - data.board.length);
            }
            const winners = determineWinners();
            recordResult(winners, data.pot, committed);
          }
        }
      }
    }
  }

  function selectHands(hands, choices, choiceColors) {
    const maxWeight = Math.max(...hands.map((x) => x.weight), 0);
    // console.log($state.snapshot(hands));
    selectedHands = {
      hands: hands.toSorted((a, b) => b.weight - a.weight),
      maxWeight,
      choices,
      choiceColors,
    };
  }

  async function clickChoice(choiceIndex) {
    // If not logged in, show login dialog instead of proceeding
    if (!account.id) {
      const { kind } = parseAction(choices[choiceIndex]);
      trackEvent("guest_demo_action", { action: kind, phase: "preflop" });
      showLoginDialog = true;
      choiceClicked = false; // Don't lock the UI
      return;
    }

    choiceClicked = true;
    const { kind } = parseAction(choices[choiceIndex]);
    trackEvent("player_action", { action: kind, phase: state });
    if (playerIndex === decisionPlayer) {
      if (state === "preflop") {
        const action = choices[choiceIndex];
        const rawMistake = strategyHand[choiceIndex] == 0;
        // Only the first player mistake counts — after that the hand is out
        // of the GTO range, so subsequent decisions can't be evaluated.
        const firstMistake = rawMistake && !mistakeTracked;
        const mistake = firstMistake;
        let mistakeLoss = null;
        if (firstMistake) {
          mistakeTracked = true;
          // Preflop: no child action values available, leave mistakeLoss as null
        }
        sequence.push(
          $state.snapshot({
            player: decisionPlayer,
            action,
            choices,
            mistake,
            mistakeLoss,
          }),
        );
        await iterateDecisions();
      } else if (state === "postflop") {
        if (
          gameGraph &&
          choices &&
          playerIndex === decisionPlayer &&
          choiceIndex < choices.length
        ) {
          const action = choices[choiceIndex];
          const rawMistake = strategyHand[choiceIndex] == 0;
          // Only the first player mistake counts — after that the hand is out
          // of the GTO range, so subsequent decisions can't be evaluated.
          const firstMistake = rawMistake && !mistakeTracked;
          const mistake = firstMistake;
          let mistakeLoss = null;
          if (firstMistake) {
            mistakeTracked = true;
            // Compute EV-based mistake loss from child counterfactual values.
            // childValues[i] = EV if action i is taken.
            // mistakeLoss = best_action_EV - chosen_action_EV (in chips).
            if (childValues && choiceIndex < childValues.length) {
              const chosenEV = childValues[choiceIndex];
              const bestEV = Math.max(...childValues);
              mistakeLoss = +(bestEV - chosenEV).toFixed(2);
            }
          }
          sequence.push(
            $state.snapshot({
              player: decisionPlayer,
              action,
              choices,
              mistake,
              mistakeLoss,
            }),
          );
          await getSolution(solutionFile, actionIndices.concat(choiceIndex));
          await iterateDecisions();
        }
      }
    }
    // Save in-progress hand after each decision
    saveInProgressHand();
  }

  function tablePositionToPlayerIndex(position) {
    return position === 0 ? playerIndex : opponentIndex;
  }
</script>

<Template title="Play poker against a GTO bot">
  {#if reviewResult && isPortrait}
    <!-- Full-screen hand review in portrait mode -->
    <div
      class="w-full h-full min-h-0 overflow-y-auto overflow-x-hidden bg-[var(--poker-bg)] text-[var(--poker-text)]"
    >
      <div class="w-full p-4">
        <HandReview
          result={reviewResult}
          {preflopData}
          {playerPositions}
          onclose={() => (reviewResult = null)}
          ontrack={trackEvent}
        />
      </div>
    </div>
  {:else}
    <div
      class="flex landscape:flex-row portrait:flex-col gap-0 w-full h-full min-h-0"
    >
      <div class="w-full flex-1 min-h-0 flex flex-col gap-0">
        <div class="flex-1 w-full h-full">
          <PokerTable>
            {#snippet slot({ position, inside, center })}
              {#if center}
                <div class="flex flex-col gap-2 justify-center items-center">
                  <div class="flex">
                    <div class="h-16 w-32">
                      <ChipVisual chips={pot} />
                    </div>
                  </div>
                  <div class="flex gap-0">
                    {#each applyFlopSwap(flopSwap, board) as card}
                      <div class="w-8 h-10">
                        <CardSVG {card} {suitShift} />
                      </div>
                    {/each}
                  </div>
                </div>
              {/if}
              {#if position === 0 || position === 5}
                {#if inside}
                  {@const index = tablePositionToPlayerIndex(position)}
                  {@const displayPlayerName = playerPositions[index]}
                  {@const lastActionResult = lastAction(sequence, index)}
                  <div
                    class="flex flex-col gap-1 items-center justify-center"
                    class:flex-col-reverse={position === 5}
                  >
                    {#if lastActionResult}
                      {@const { action, amount } = lastActionResult}
                      {@const { kind, value } = parseAction(action)}
                      {@const color = colorForAction([action], 0)}
                      <div class="flex items-center justify-center gap-1">
                        <div class="h-16 w-32">
                          <ChipVisual chips={amount} />
                        </div>
                        <PlayerAction labels={[kind, value]} {color} />
                      </div>
                    {:else}
                      <!-- placeholder -->
                      <div class="h-16 w-32"></div>
                    {/if}
                    <div class="flex items-center justify-center gap-1">
                      <div class="h-16 w-32">
                        <ChipVisual chips={chips ? chips[index] : 0} />
                      </div>
                      <div class="flex gap-0">
                        {#if hands}
                          {#each hands[index] as card}
                            <div class="w-8 h-10">
                              <CardSVG
                                card={index === playerIndex || result
                                  ? card
                                  : null}
                                {suitShift}
                              />
                            </div>
                          {/each}
                        {/if}
                      </div>
                    </div>
                    <div class="font-bold text-white text-shadow-lg">
                      {displayPlayerName}
                      {#if index === playerIndex}
                        (you)
                      {/if}
                    </div>
                  </div>
                {/if}
              {/if}
            {/snippet}
          </PokerTable>
        </div>
        <div
          class="flex-none h-16 p-2 bg-[var(--poker-bg)] flex items-center justify-evenly"
        >
          {#if choices && decisionPlayer !== null}
            <div class="flex gap-2 items-center justify-center">
              {#each choices as choice, i}
                {@const { kind, value } = parseAction(choice)}
                {@const color = colorForAction(choices, i)}
                {#if kind === "Fold"}
                  <DecisionButton
                    label={`${kind}${value ? " (" + value + ")" : ""}`}
                    {color}
                    disabled={account.id
                      ? decisionPlayer !== playerIndex || choiceClicked
                      : false}
                    onclick={() => clickChoice(i)}
                  />
                {/if}
              {/each}
            </div>
            <div class="flex gap-2 items-center justify-center">
              {#each choices as choice, i}
                {@const { kind, value } = parseAction(choice)}
                {@const color = colorForAction(choices, i)}
                {#if ["Check", "Call"].includes(kind)}
                  <DecisionButton
                    label={`${kind}${value ? " (" + value + ")" : ""}`}
                    {color}
                    disabled={account.id
                      ? decisionPlayer !== playerIndex || choiceClicked
                      : false}
                    onclick={() => clickChoice(i)}
                  />
                {/if}
              {/each}
            </div>
            <div class="flex gap-2 items-center justify-center">
              {#each choices as choice, i}
                {@const { kind, value } = parseAction(choice)}
                {@const color = colorForAction(choices, i)}
                {#if ["Bet", "Raise", "All In"].includes(kind)}
                  <DecisionButton
                    label={`${kind}${value ? " (" + value + ")" : ""}`}
                    {color}
                    disabled={account.id
                      ? decisionPlayer !== playerIndex || choiceClicked
                      : false}
                    onclick={() => clickChoice(i)}
                  />
                {/if}
              {/each}
            </div>
          {:else if state === "complete"}
            <div>
              <DecisionButton
                onclick={() => {
                  if (!account.id) {
                    showLoginDialog = true;
                  } else {
                    trackEvent("new_hand_click");
                    newHand();
                  }
                }}
                label="New hand"
              />
            </div>
          {/if}
        </div>
      </div>
      <div
        class="flex-none landscape:w-1/3 portrait:h-1/3 min-h-0 overflow-y-auto overflow-x-hidden p-2 bg-[var(--poker-bg)] text-[var(--poker-text)]"
      >
        <div class="flex flex-col space-y-4 min-h-0">
          {#if account.id}
            <div class="flex gap-2 justify-center">
              {#each tabItems as tab}
                <button
                  class="px-4 py-1 text-sm font-medium rounded-full transition-all duration-200"
                  class:bg-transparent={tab.value !== activeTab}
                  style="color: {tab.value === activeTab
                    ? 'var(--poker-text)'
                    : 'color-mix(in srgb, var(--poker-text) 40%, transparent)'}; background-color: {tab.value ===
                  activeTab
                    ? 'color-mix(in srgb, var(--poker-text) 12%, transparent)'
                    : 'transparent'}"
                  onclick={() => {
                    activeTab = tab.value;
                    if (tab.value === "results")
                      trackEvent("results_tab_click");
                    if (tab.value === "actions")
                      trackEvent("actions_tab_click");
                  }}
                >
                  {tab.label}
                </button>
              {/each}
            </div>
            {#if activeTab === "actions"}
              <div class="flex flex-wrap gap-2">
                {#each sequence as { action, player, chance }, i}
                  {#if action}
                    {@const { kind, value } = parseAction(action)}
                    {@const color = colorForAction([action], 0)}
                    <PlayerAction
                      playerName={playerPositions[player]}
                      labels={[kind, value]}
                      {color}
                    />
                  {:else if chance}
                    <div class="w-full h-0"></div>
                    <div class="flex gap-0">
                      {#each chance.length === 3 && flopSwap ? applyFlopSwap(flopSwap, chance) : chance as card}
                        <div class="w-8 h-10">
                          <CardSVG {card} {suitShift} />
                        </div>
                      {/each}
                    </div>
                    <div class="w-full h-0"></div>
                  {/if}
                {/each}
              </div>
            {:else if activeTab === "results"}
              {#if reviewResult}
                <HandReview
                  result={reviewResult}
                  {preflopData}
                  {playerPositions}
                  onclose={() => (reviewResult = null)}
                  ontrack={trackEvent}
                />
              {:else}
                <div class="w-full flex gap-2 items-center justify-start">
                  <span>Profit: {totalProfit}</span>
                  {#if totalMistakes > 0}
                    <span style="color: #f97316;" class="ml-2"
                      >Mistakes: {totalMistakes}</span
                    >
                  {/if}
                </div>
                {#if results.length > 0}
                  <ProfitChart data={cumulativeProfitData} />
                {/if}

                <p class="text-xs text-gray-400 mt-2">
                  Results are stored in your browser (IndexedDB). They won't be
                  available if you switch computers or browsers, and won't
                  survive if you clear the site's application data. You can
                  download or upload your results below to preserve them.
                </p>

                <input
                  type="file"
                  accept=".json,application/json"
                  class="hidden"
                  bind:this={fileInput}
                  onchange={handleFileUpload}
                />

                <div class="flex items-center gap-2 mt-3 text-xs text-gray-400">
                  <button
                    onclick={() => {
                      trackEvent("upload_results_click");
                      fileInput?.click();
                    }}
                    class="flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-700 text-gray-400 hover:text-green-400 transition-colors"
                    title="Upload hands from JSON"
                  >
                    <UploadIcon size={14} />
                    Upload
                  </button>
                  <span
                    >Uploading will replace any hands you currently have.</span
                  >
                </div>

                {#if results.length > 0}
                  <div class="w-full mt-4">
                    <div
                      class="flex items-center justify-between text-sm font-semibold mb-1"
                    >
                      <span>Hands ({results.length})</span>
                      {#if results.length > 0}
                        <div class="flex items-center gap-1">
                          <button
                            onclick={() => {
                              trackEvent("download_results_click");
                              downloadResults();
                            }}
                            class="p-1 rounded hover:bg-gray-700 text-gray-400 hover:text-blue-400 transition-colors"
                            title="Download hands as JSON"
                          >
                            <DownloadIcon size={14} />
                          </button>
                          <button
                            onclick={() => {
                              trackEvent("delete_results_click");
                              showClearDialog = true;
                            }}
                            class="p-1 rounded hover:bg-gray-700 text-gray-400 hover:text-red-400 transition-colors"
                            title="Clear all hands"
                          >
                            <Trash2Icon size={14} />
                          </button>
                        </div>
                      {/if}
                    </div>
                    <div class="overflow-x-auto">
                      <table class="w-full text-xs">
                        <thead>
                          <tr class="border-b border-gray-600">
                            <th class="text-left py-1 pr-1 whitespace-nowrap"
                              >Player</th
                            >
                            <th class="text-left py-1 pr-1 whitespace-nowrap"
                              >Opp</th
                            >
                            <th class="text-left py-1 pr-1 whitespace-nowrap"
                              >Board</th
                            >
                            <th class="text-right py-1 pl-1 whitespace-nowrap"
                              >Profit</th
                            >
                            <th class="text-right py-1 pl-1 whitespace-nowrap"
                              >Mistake</th
                            >
                          </tr>
                        </thead>
                        <tbody>
                          {#each paginatedResults as r}
                            <tr
                              class="border-b border-gray-700 cursor-pointer hover:bg-gray-700 transition-colors"
                              onclick={() => {
                                reviewResult = r;
                                trackEvent("hand_review_click");
                              }}
                            >
                              <td class="py-1 pr-1">
                                <div class="flex gap-0">
                                  {#each r.playerCards as card}
                                    <div class="w-5 h-7">
                                      <CardSVG
                                        {card}
                                        suitShift={r.suitShift}
                                        border={false}
                                      />
                                    </div>
                                  {/each}
                                </div>
                              </td>
                              <td class="py-1 pr-1">
                                <div class="flex gap-0">
                                  {#each r.opponentCards as card}
                                    <div class="w-5 h-7">
                                      <CardSVG
                                        {card}
                                        suitShift={r.suitShift}
                                        border={false}
                                      />
                                    </div>
                                  {/each}
                                </div>
                              </td>
                              <td class="py-1 pr-1">
                                <div class="flex gap-0">
                                  {#each applyFlopSwap(r.flopSwap, r.boardCards) as card}
                                    <div class="w-5 h-7">
                                      <CardSVG
                                        {card}
                                        suitShift={r.suitShift}
                                        border={false}
                                      />
                                    </div>
                                  {/each}
                                </div>
                              </td>
                              <td
                                class="text-right py-1 pl-1 font-mono whitespace-nowrap"
                              >
                                {r.profits["player"] > 0 ? "+" : ""}{r.profits[
                                  "player"
                                ]}
                              </td>
                              <td
                                class="text-right py-1 pl-1 font-mono whitespace-nowrap"
                                style={r.mistakeLoss != null
                                  ? "color: #f97316; font-weight: bold;"
                                  : ""}
                              >
                                {r.mistakeLoss != null ? r.mistakeLoss : ""}
                              </td>
                            </tr>
                          {/each}
                        </tbody>
                      </table>
                    </div>

                    {#if totalResultsPages > 1}
                      <div
                        class="flex items-center justify-center gap-2 mt-2 text-xs"
                      >
                        <button
                          class="px-2 py-1 rounded disabled:opacity-30 hover:bg-gray-700"
                          disabled={resultsPage <= 0}
                          onclick={() =>
                            (resultsPage = Math.max(0, resultsPage - 1))}
                        >
                          &lt; Prev
                        </button>
                        <span class="text-gray-400">
                          Page {resultsPage + 1} of {totalResultsPages}
                        </span>
                        <button
                          class="px-2 py-1 rounded disabled:opacity-30 hover:bg-gray-700"
                          disabled={resultsPage >= totalResultsPages - 1}
                          onclick={() =>
                            (resultsPage = Math.min(
                              totalResultsPages - 1,
                              resultsPage + 1,
                            ))}
                        >
                          Next &gt;
                        </button>
                      </div>
                    {/if}
                  </div>
                {/if}
              {/if}
            {/if}
          {:else}
            <div
              class="flex flex-col items-center justify-center h-full p-6 text-center text-[var(--poker-text)] overflow-y-auto"
            >
              <h2 class="text-2xl font-bold mb-1">Welcome to ActionFlop</h2>
              <p class="text-sm dark:text-gray-400 text-gray-600 max-w-sm">
                ActionFlop is a poker training tool. Play heads-up no limit
                holdem against a GTO bot.
              </p>
              <p class="text-sm dark:text-gray-400 text-gray-600 max-w-sm mt-2">
                Every decision the bot makes is game theory optimal — a
                mathematically balanced, unexploitable strategy that converts
                your mistakes into steady, long-term profit. Each hand starts
                fresh at 100 BB; stacks always reset.
              </p>
              <p class="text-sm dark:text-gray-400 text-gray-600 max-w-sm mt-2">
                Your profit and loss are tracked in a live graph as you play.
                After each hand, review your history and analyze every decision
                node — see the range and strategy at each point so you can
                understand exactly where you went wrong.
              </p>

              <!-- Stats -->
              <div class="w-full mt-6">
                <div class="grid grid-cols-3 gap-3">
                  <div
                    class="rounded-xl dark:bg-gray-800/60 bg-gray-200/60 dark:border-gray-700/50 border-gray-300/50 p-3 flex flex-col items-center"
                  >
                    <div
                      class="text-xl sm:text-2xl font-bold dark:text-white text-gray-900 tracking-tight"
                    >
                      1.8M+
                    </div>
                    <div
                      class="text-[10px] sm:text-xs dark:text-gray-400 text-gray-600 mt-1 leading-tight text-center"
                    >
                      Hands Played
                    </div>
                  </div>
                  <div
                    class="rounded-xl dark:bg-gray-800/60 bg-gray-200/60 dark:border-gray-700/50 border-gray-300/50 p-3 flex flex-col items-center"
                  >
                    <div
                      class="text-xl sm:text-2xl font-bold dark:text-emerald-400 text-emerald-600 tracking-tight"
                    >
                      +2.3M
                    </div>
                    <div
                      class="text-[10px] sm:text-xs dark:text-gray-400 text-gray-600 mt-1 leading-tight text-center"
                    >
                      Bot Profit (63 BB/100)
                    </div>
                  </div>
                  <div
                    class="rounded-xl dark:bg-gray-800/60 bg-gray-200/60 dark:border-gray-700/50 border-gray-300/50 p-3 flex flex-col items-center"
                  >
                    <div
                      class="text-xl sm:text-2xl font-bold dark:text-white text-gray-900 tracking-tight"
                    >
                      24K+
                    </div>
                    <div
                      class="text-[10px] sm:text-xs dark:text-gray-400 text-gray-600 mt-1 leading-tight text-center"
                    >
                      Players
                    </div>
                  </div>
                </div>
              </div>

              <!-- CTA -->
              <p class="text-sm dark:text-gray-400 text-gray-600 mt-6">
                Log in to start training and tracking your progress.
              </p>
              <a
                href="/auth/login/google?return_url=/"
                class="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md border border-gray-600 bg-white text-gray-800 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 dark:border-gray-500 transition-colors text-sm font-medium"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 48 48"
                >
                  <path
                    fill="#4285F4"
                    d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
                  />
                  <path
                    fill="#34A853"
                    d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"
                  />
                  <path
                    fill="#EA4335"
                    d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
                  />
                </svg>
                Continue with Google
              </a>
            </div>
          {/if}
        </div>
      </div>
    </div>
  {/if}
</Template>

<ResponsiveDialog
  title="Clear All Hands"
  description="Are you sure you want to delete all your hands?"
  bind:open={showClearDialog}
>
  {#snippet content()}
    <div class="flex flex-col gap-3">
      <div class="flex justify-end gap-2 pt-2">
        <Button
          variant="outline"
          onclick={() => (showClearDialog = false)}
          disabled={clearing}
        >
          Cancel
        </Button>
        <Button
          variant="destructive"
          onclick={clearAllResults}
          disabled={clearing}
        >
          {#if clearing}
            <Loader2 class="size-4 animate-spin mr-2" />
          {/if}
          Clear All
        </Button>
      </div>
    </div>
  {/snippet}
</ResponsiveDialog>

<ResponsiveDialog title="Out of Credits" bind:open={showCreditsExhaustedDialog}>
  {#snippet content()}
    <div class="flex flex-col gap-3">
      <p class="text-sm text-muted-foreground">
        You are all out of credits until next month. Visit your <a
          href="/account">Account</a
        > to see your credit usage, or if you would like to upgrade.
      </p>
      <div class="flex justify-end gap-2 pt-2">
        <Button
          variant="outline"
          onclick={() => (showCreditsExhaustedDialog = false)}
        >
          Close
        </Button>
        <a
          href="/account#credits"
          class="inline-flex h-9 items-center justify-center rounded-lg bg-foreground text-background px-4 py-2 text-sm font-medium transition-colors hover:bg-foreground/85 shadow-none"
          onclick={() => (showCreditsExhaustedDialog = false)}
        >
          Go to Account
        </a>
      </div>
    </div>
  {/snippet}
</ResponsiveDialog>

<ResponsiveDialog
  title="Log in to Play"
  description="You need an account to play."
  bind:open={showLoginDialog}
>
  {#snippet content()}
    <div class="flex flex-col gap-4">
      <p class="text-sm text-muted-foreground">
        Log in with your Google account to start playing against GTO bots and
        track your results.
      </p>
      <div class="flex items-center justify-end gap-2 pt-2">
        <Button
          variant="outline"
          onclick={() => {
            trackEvent("login_maybe_later");
            showLoginDialog = false;
          }}
        >
          Maybe Later
        </Button>
        <Button
          class="h-12"
          href="/auth/login/google?return_url=/"
          onclick={() => trackEvent("login_continue_google")}
        >
          <img src="/images/google.svg" class="h-5 mr-2" alt="Google" />
          Continue with Google
        </Button>
      </div>
    </div>
  {/snippet}
</ResponsiveDialog>
